import React, { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChevronRight } from "lucide-react";
import { useEa } from "@/components/energy-analytics/analytics/EaContext";
import { EaMultiYearCard } from "@/components/energy-analytics/analytics/EaMultiYearCard";
import { useEaData } from "@/components/energy-analytics/analytics/useEaData";
import { EaChartCard, EaNote, EaPageHeader, EaUnitToggle } from "@/components/energy-analytics/analytics/EaUi";
import { EaExportButton, EaPointPicker, useSelectedCodes } from "@/components/energy-analytics/analytics/EaControls";
import { COLORS, MONTHS_IT, POWER_UNITS, fmtPower, fmtQty, toPowerUnit } from "@/components/energy-analytics/analytics/eaFormat";
import { PageState } from "./EaPowerOverview";
import { EaYoyCard } from "@/components/energy-analytics/analytics/EaYoyCard";

const F_COLORS = { f1: "#5B16D0", f2: "#8B32FB", f3: "#D3ABFD" };
const MONTHS_FULL = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const axis = { tick: { fontSize: 11, fill: "#64748B" }, axisLine: false, tickLine: false };

function Crumbs({ year, month, day, setFilter }) {
  const items = [{ label: `Anno ${year}`, on: () => setFilter({ month: "", day: "" }), id: "year" }];
  if (month) items.push({ label: MONTHS_FULL[month - 1], on: () => setFilter({ day: "" }), id: "month" });
  if (day) items.push({ label: day.split("-").reverse().join("/"), id: "day" });
  return (
    <div className="flex items-center gap-1 text-sm" data-testid="ea-cons-breadcrumb">
      {items.map((it, i) => (
        <React.Fragment key={it.id}>
          {i > 0 && <ChevronRight size={14} className="text-slate-400" />}
          <button onClick={it.on} disabled={!it.on || i === items.length - 1} data-testid={`ea-crumb-${it.id}`}
            className={i === items.length - 1 ? "font-semibold text-slate-900" : "text-blue-600 hover:underline"}>{it.label}</button>
        </React.Fragment>
      ))}
    </div>
  );
}

function YearChart({ d, unit, onMonth }) {
  const conv = (v) => toPowerUnit(v, unit);
  const data = d.monthly.map((m) => ({ ...m, label: MONTHS_IT[m.month - 1], f1: conv(m.f1), f2: conv(m.f2), f3: conv(m.f3), fc: m.status === "FORECAST" }));
  return (
    <div className="h-80" data-testid="ea-cons-chart-year">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey="label" {...axis} />
          <YAxis {...axis} width={70} tickFormatter={(v) => fmtQty(v, unit === "GWh" ? 2 : 0)} label={{ value: unit, angle: -90, position: "insideLeft", fontSize: 11, fill: "#64748B" }} />
          <Tooltip formatter={(v, n) => [fmtQty(v, POWER_UNITS[unit].digits, unit), n]} labelFormatter={(l, p) => `${l}${p?.[0]?.payload?.fc ? " · previsionale" : " · consuntivo"}`} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {["f1", "f2", "f3"].map((f) => (
            <Bar key={f} dataKey={f} name={f.toUpperCase()} stackId="f" fill={F_COLORS[f]} className="cursor-pointer" onClick={(e) => onMonth(e.payload.month)}>
              {data.map((r) => <Cell key={r.month} fillOpacity={r.fc ? 0.3 : 1} stroke={r.fc ? F_COLORS[f] : undefined} strokeDasharray={r.fc ? "3 2" : undefined} />)}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function MonthChart({ d, unit, onDay }) {
  const data = d.daily.map((x) => ({ ...x, label: x.date.slice(8), v: toPowerUnit(x.kwh, unit) }));
  return (
    <div className="h-80" data-testid="ea-cons-chart-month">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey="label" {...axis} />
          <YAxis {...axis} width={70} tickFormatter={(v) => fmtQty(v, unit === "GWh" ? 2 : 0)} />
          <Tooltip formatter={(v) => [fmtQty(v, POWER_UNITS[unit].digits, unit), "Consumo"]} labelFormatter={(l, p) => `${p?.[0]?.payload?.date?.split("-").reverse().join("/") || l}${p?.[0]?.payload?.incomplete ? " · giorno incompleto" : ""}`} />
          <Bar dataKey="v" name="Consumo giornaliero" fill={COLORS.power} radius={[3, 3, 0, 0]} className="cursor-pointer" onClick={(e) => onDay(e.payload.date)}>
            {data.map((r) => <Cell key={r.date} fillOpacity={r.incomplete ? 0.45 : 1} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function DayChart({ day, gran, metric }) {
  const rows = gran === "h" ? day.hourly.map((h) => ({ label: h.hour, v: metric === "kw" ? h.kw_max : h.kwh }))
    : day.quarters.map((q) => ({ label: q.time, v: metric === "kw" ? q.kw : q.kwh }));
  const u = metric === "kw" ? "kW" : "kWh";
  return (
    <div className="h-80" data-testid="ea-cons-chart-day">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey="label" {...axis} interval={gran === "h" ? 0 : 7} />
          <YAxis {...axis} width={60} tickFormatter={(v) => fmtQty(v, 0)} label={{ value: u, angle: -90, position: "insideLeft", fontSize: 11, fill: "#64748B" }} />
          <Tooltip formatter={(v) => [fmtQty(v, 1, u), metric === "kw" ? (gran === "h" ? "Potenza max" : "Potenza media") : "Consumo"]} />
          <Bar dataKey="v" fill={COLORS.power} radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Seg({ value, options, onChange, testid }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid={testid}>
      {options.map(([v, l]) => (
        <button key={v} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
          className={`px-3 py-1 text-xs font-semibold rounded-md ${value === v ? "bg-[#071B4D] text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
      ))}
    </div>
  );
}

export default function EaPowerConsumption() {
  const { unit, setUnit, filters, setFilter } = useEa();
  const codes = useSelectedCodes("POWER");
  const mNum = Number(filters.month);
  const month = mNum >= 1 && mNum <= 12 ? mNum : null;
  const day = month && /^\d{4}-\d{2}-\d{2}$/.test(filters.day) ? filters.day : "";
  const [cy, setCy] = useState(null);
  const { filters: { year: selYear } } = useEa();
  useEffect(() => setCy(null), [selYear]);
  const { data: d, loading, error } = useEaData("/ea/power/consumption", { codes, month: month || "", date: day, compare_year: cy || "" });
  const [gran, setGran] = useState("h");
  const [metric, setMetric] = useState("kwh");
  const header = (
    <EaPageHeader title="Consumi Power" subtitle="Anno → mese → giorno → ore e quarti d'ora. Clicca su una barra per scendere di livello."
      units={<EaUnitToggle unit={unit} setUnit={setUnit} />} right={<EaPointPicker commodity="POWER" />} />
  );
  let exp = null;
  if (d) {
    if (day && d.day) exp = { name: `power_consumi_${day}`, cols: gran === "h" ? [{ key: "hour", label: "Ora" }, { key: "kwh", label: "kWh" }, { key: "kw_max", label: "kW max" }] : [{ key: "time", label: "Ora" }, { key: "kwh", label: "kWh" }, { key: "kw", label: "kW" }], rows: gran === "h" ? d.day.hourly.map((h) => ({ ...h, hour: `${h.hour}:00` })) : d.day.quarters };
    else if (month && d.daily) exp = { name: `power_consumi_${d.year}_${month}`, cols: [{ key: "date", label: "Data" }, { key: "kwh", label: "kWh" }, { key: "peak_kw", label: "Picco kW" }, { key: "incomplete", label: "Incompleto" }], rows: d.daily };
    else exp = { name: `power_consumi_${d.year}`, cols: [{ key: "month", label: "Mese" }, { key: "status", label: "Stato" }, { key: "f1", label: "F1 kWh" }, { key: "f2", label: "F2 kWh" }, { key: "f3", label: "F3 kWh" }, { key: "total", label: "Totale kWh" }], rows: d.monthly };
  }
  return (
    <PageState header={header} loading={loading} error={error} data={d} testid="ea-power-consumption">
      {d && <>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Crumbs year={d.year} month={month} day={day} setFilter={setFilter} />
          {exp && <EaExportButton filename={exp.name} commodity="POWER" year={d.year} period={day ? day.split("-").reverse().join("/") : undefined} columns={exp.cols} rows={exp.rows} testid="ea-cons-export" />}
        </div>
        {!month && (
          <EaChartCard title="Consumo mensile per fascia" subtitle={`Pieno = consuntivo, tenue tratteggiato = previsionale · ${unit}`} testid="ea-cons-card-year">
            <YearChart d={d} unit={unit} onMonth={(m) => setFilter({ month: m, day: "" })} />
          </EaChartCard>
        )}
        {!month && <EaYoyCard yoy={d.yoy} title="Confronto anno su anno · consumi consuntivi" fmt={(v) => fmtPower(v, unit)} conv={(v) => toPowerUnit(v, unit)}
          unitLabel={unit} tickDigits={unit === "GWh" ? 2 : 0} color={COLORS.power} commodity="POWER" filename={`power_consumi_yoy_${d.year}`} testid="ea-cons-yoy" compareYears={d.compare_years} onCompare={setCy} />}
        {!month && <EaMultiYearCard commodity="POWER" codes={codes} color={COLORS.power} testid="ea-cons-multiyear" metrics={[{ key: "consumption", label: "Consumi", conv: (v) => toPowerUnit(v, unit), fmt: (v) => fmtQty(v, unit === "kWh" ? 0 : 1, unit) }]} />}
        {month && !day && (
          <EaChartCard title={`Consumo giornaliero · ${MONTHS_FULL[month - 1]} ${d.year}`} subtitle="Dai quartorari. Barre tenui = giorni incompleti." testid="ea-cons-card-month">
            {d.daily?.length ? <MonthChart d={d} unit={unit} onDay={(x) => setFilter({ day: x })} /> : <EaNote>Dati quartorari non disponibili per questo mese.</EaNote>}
          </EaChartCard>
        )}
        {day && (
          <EaChartCard title={`Profilo del ${day.split("-").reverse().join("/")}`} testid="ea-cons-card-day"
            subtitle={d.day ? `Totale ${fmtQty(d.day.total_kwh, 0, "kWh")}${d.day.slot_mapping === "DST_SPRING_SEQUENTIAL" ? " · cambio ora legale (23 ore)" : ""}${d.day.data_quality !== "COMPLETE" ? " · giorno incompleto" : ""}` : ""}
            right={<div className="flex gap-2"><Seg value={gran} onChange={setGran} options={[["h", "Ore"], ["q", "Quarti d'ora"]]} testid="ea-day-gran" /><Seg value={metric} onChange={setMetric} options={[["kwh", "kWh"], ["kw", "kW"]]} testid="ea-day-metric" /></div>}>
            {d.day ? <DayChart day={d.day} gran={gran} metric={metric} /> : <EaNote>Nessun dato quartorario per questo giorno.</EaNote>}
          </EaChartCard>
        )}
      </>}
    </PageState>
  );
}
